import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usd } from "@/components/ai/useAiEdits";
import { errMsg, useApi } from "@/lib/api";
import { fmtN } from "./helpers";

function BudgetCell({ row, onChanged }) {
  const api = useApi();
  const [v, setV] = useState(row.budget ?? "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/projects/${row.project_id}/ai-budget`, { usd: v === "" ? null : Number(String(v).replace(",", ".")) });
      toast.success(data.project_budget ? `Budget di «${row.name}»: ${usd(data.project_budget)}` : `Budget di «${row.name}» rimosso`);
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const pct = row.budget ? Math.round((row.total_all_time / row.budget) * 100) : null;
  return (
    <div className="flex items-center justify-end gap-1.5">
      {pct != null && <span className={`text-[11px] tabular-nums ${pct >= 100 ? "text-red-600" : pct >= 80 ? "text-amber-700" : "text-slate-500"}`} data-testid={`project-budget-pct-${row.project_id}`}>{pct}%</span>}
      <Input type="number" min="0" step="0.5" value={v} onChange={(e) => setV(e.target.value)} placeholder="Nessuno" className="h-8 w-28 text-xs text-right" data-testid={`project-budget-input-${row.project_id}`} />
      <Button size="sm" variant="outline" className="h-8 px-2.5" disabled={busy || String(v) === String(row.budget ?? "")} onClick={save} data-testid={`project-budget-save-${row.project_id}`}>
        {busy ? <Loader2 size={13} className="animate-spin" /> : "Salva"}
      </Button>
    </div>
  );
}

export default function ProjectsTable({ rows, onChanged }) {
  const real = (r) => r.project_id !== "-" && r.name !== "(progetto eliminato)";
  return (
    <section className="rounded-xl border border-slate-200 bg-white" data-testid="costs-projects">
      <div className="p-5 pb-3"><h2 className="text-base font-semibold text-slate-900">Per progetto</h2>
        <p className="mt-1 text-[11px] text-slate-500">Il budget del progetto vale su tutta la sua storia: oltre il budget le nuove operazioni AI vengono bloccate dal server.</p></div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-5 py-2 text-left font-medium">Progetto</th><th className="px-3 py-2 text-right font-medium">Nel periodo</th><th className="px-3 py-2 text-right font-medium">Totale storico</th><th className="px-3 py-2 text-right font-medium">Chiamate</th><th className="px-5 py-2 text-right font-medium">Budget USD</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!rows.length && <tr><td colSpan={5} className="px-5 py-6 text-center text-sm text-slate-500">Nessun consumo nel periodo selezionato.</td></tr>}
            {rows.map((r) => (
              <tr key={r.project_id} data-testid={`costs-project-row-${r.project_id}`}>
                <td className="px-5 py-2.5 max-w-[260px] truncate">{real(r) ? <Link to={`/projects/${r.project_id}`} className="text-slate-900 hover:text-sky-700">{r.name}</Link> : <span className="text-slate-500">{r.name}</span>}</td>
                <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{usd(r.usd)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{usd(r.total_all_time)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{fmtN(r.calls)}</td>
                <td className="px-5 py-2">{real(r) ? <BudgetCell key={`${r.project_id}-${r.budget}`} row={r} onChanged={onChanged} /> : <span className="block text-right text-xs text-slate-400">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
